import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { isDemoMode, enableDemoMode as setDemoMode, disableDemoMode as clearDemoMode, DEMO_USER } from '@/lib/demo-data';
import { supabase } from '@/integrations/supabase/client';

interface DemoContextType {
  isDemo: boolean;
  demoRole: 'user' | 'advertiser';
  setDemoRole: (role: 'user' | 'advertiser') => void;
  enableDemo: (role?: 'user' | 'advertiser') => void;
  disableDemo: () => void;
  demoUser: typeof DEMO_USER | null;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

export function DemoProvider({ children }: { children: ReactNode }) {
  const [isDemo, setIsDemo] = useState(isDemoMode());
  const [demoRole, setDemoRoleState] = useState<'user' | 'advertiser'>(() => {
    return (localStorage.getItem('pingEarn_demo_role') as 'user' | 'advertiser') || 'user';
  });

  useEffect(() => {
    setIsDemo(isDemoMode());
  }, []);

  // Auto-generate demo pings every 2 minutes
  useEffect(() => {
    if (!isDemo) return;
    
    const interval = setInterval(() => {
      console.log('Demo Mode: Auto-generating new ping');
      // Trigger a custom event for demo ping generation
      window.dispatchEvent(new CustomEvent('demo-ping-generated'));
    }, 120000); // 2 minutes

    return () => clearInterval(interval);
  }, [isDemo]);

  // Listen for real authentication and disable demo mode
  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      // If user signs in or signs up with a real account, disable demo mode
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
        if (isDemo) {
          console.log('Real user authenticated, disabling demo mode');
          disableDemo();
        }
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [isDemo]);

  const setDemoRole = (role: 'user' | 'advertiser') => {
    setDemoRoleState(role);
    localStorage.setItem('pingEarn_demo_role', role);
  };

  const enableDemo = (role: 'user' | 'advertiser' = 'user') => {
    setDemoMode();
    setIsDemo(true);
    setDemoRole(role);
  };

  const disableDemo = () => {
    clearDemoMode();
    setIsDemo(false);
    localStorage.removeItem('pingEarn_demo_role');
  };

  return (
    <DemoContext.Provider value={{ 
      isDemo,
      demoRole,
      setDemoRole,
      enableDemo, 
      disableDemo,
      demoUser: isDemo ? DEMO_USER : null 
    }}>
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const context = useContext(DemoContext);
  if (context === undefined) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
}
